import { useState, useEffect, useMemo } from "react";
// Neutral grey placeholder (no running-man, no dead remote service).
import { GREY_PLACEHOLDER as defaultImage } from "../../constants/imageFallback";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { userService, clubService } from "../../services/api";
import { ROLES } from "../../config/roles";
import AgencyPublicBanner from "../../components/common/AgencyPublicBanner";
import FifaVerifiedBadge, {
  isFifaVerified,
} from "../../components/common/FifaVerifiedBadge";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import AgentPlayerCard from "../../components/agent/AgentPlayerCard";
 
import { useMediaQuery } from "../../hooks/useMediaQuery";
import CustomDropdown from "../../components/common/CustomDropdown";
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation, getStaticTemplateMatch } from "../../translations/registerStaticTranslations";

const AgencyPublicProfile = () => {
  const { agencyId } = useParams();
  const { currentRole } = useSelector((state) => state.auth);
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — look the sentence up the same way the scanner does (exact
  // match first, template match second, e.g. for the "{count}" share count).
  const currentLanguage = useUiLanguage();
  const tr = (s) =>
    getStaticTranslation(i18n, s, currentLanguage) ||
    getStaticTemplateMatch(i18n, s, currentLanguage) ||
    s;
  const [activeTab, setActiveTab] = useState("agents");
  const [activeAgentType, setActiveAgentType] = useState("licensed");
  const [selectedPosition, setSelectedPosition] = useState("all");
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Default agency data object (since no API is available yet)
  const defaultAgencyData = useMemo(
    () => ({
      id: agencyId,
      name: "Wasserman Agency",
      image_path: defaultImage,
      country: "United States",
      overall_ranking: "Top 5",
      // Self-declaration wording only — never a "FIFA" claim (D-A follow-up).
      license: "Licensed",
      // T053: no placeholder TP figure. This object stands in when the
      // agency request fails, and a made-up points number under the
      // factual "TP Points" label is exactly the contradiction this
      // ticket is about — the banner now renders "-" when there is no
      // real figure.
      agents: {
        licensed: [
          {
            id: 1,
            name: "Benoit Deschasaux",
            phone: "+1 (555) 123-4567",
            email: "benoit@wasserman.com",
            license_number: "FIFA-123456",
            isAdmin: true,
            managingPlayers: [
              { name: "Player 1", image: null },
              { name: "Player 2", image: null },
              { name: "Player 3", image: null },
            ],
          },
          {
            id: 2,
            name: "Joel Barwick",
            phone: "+1 (555) 234-5678",
            email: "joel@wasserman.com",
            license_number: "FIFA-234567",
            isAdmin: false,
            managingPlayers: [
              { name: "Player 4", image: null },
              { name: "Player 5", image: null },
            ],
          },
        ],
        unlicensed: [],
      },
      partners: [
        {
          id: 1,
          name: "Maxim Berg",
          phone_number: "+34 (91) 123-4567",
          email: "maxim@wasserman.com",
          job_title: "Senior Partner",
          address: "123 Business Ave",
          city: "Los Angeles",
          country: "United States",
          zipcode: "90210",
        },
        {
          id: 2,
          name: "Alex Suprun",
          phone_number: "+34 (91) 234-5678",
          email: "alex@wasserman.com",
          job_title: "Partner",
          address: "456 Corporate Blvd",
          city: "New York",
          country: "United States",
          zipcode: "10001",
        },
      ],
      players: [],
    }),
    [agencyId]
  );

  useEffect(() => {
    const fetchAgencyData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await clubService.getAgencyPublicProfile(agencyId);
        if (response.success) {
          // API returns { results: { agency: { details, agents, players, partners } } }
          const agencyData = response.data.agency;
          if (agencyData) {
            // Transform the API data to match our component structure
            const transformedAgency = {
              id: agencyData.details?.agency_id || agencyId,
              name: agencyData.details?.name || "Agency Name",
              country: agencyData.details?.country || "N/A",
              profile_picture_url: agencyData.details?.profile_picture_url,
              overall_ranking: agencyData.details?.ranking || "N/A",
              // D-A follow-up (Marin, 2026-08-04): has_fifa_license is the
              // agency's SELF-DECLARATION and carries no licence number —
              // no "FIFA" claim from it (the FIFA wording is reserved for
              // the verified agent badge), and no negative line either:
              // null makes the banner omit the License card entirely
              // (hero precedent — absence renders nothing).
              license: agencyData.details?.has_fifa_license
                ? "Licensed"
                : null,
              tp_points: agencyData.details?.total_transfer_points || 0,
              email: agencyData.details?.email,
              website: agencyData.details?.website,
              address: agencyData.details?.address,
              city: agencyData.details?.city,
              zipcode: agencyData.details?.zipcode,
              // Transform agents data
              agents: {
                licensed:
                  agencyData.agents?.filter((agent) => agent.license_number) ||
                  [],
                unlicensed:
                  agencyData.agents?.filter((agent) => !agent.license_number) ||
                  [],
              },
              // Transform players data
              players: agencyData.players || [],
              // Transform partners data
              partners: agencyData.partners || [],
            };
            setAgency(transformedAgency);
          } else {
            // Fallback to default data if API returns no agency data
            setAgency(defaultAgencyData);
          }
        } else {
          // Fallback to default data if API fails
          setAgency(defaultAgencyData);
        }
      } catch (error) {
        console.error("Error fetching agency data:", error);
        setError("Failed to load agency data.");
        // Fallback to default data
        setAgency(defaultAgencyData);
      }

      setLoading(false);
    };

    if (agencyId) {
      fetchAgencyData();
    }
  }, [agencyId, defaultAgencyData]);

  // Fetch current user profile for conditional Share button
  useEffect(() => {
    const fetchProfile = async () => {
      const res = await userService.getProfile();
      if (res.success) setCurrentUserProfile(res.data);
    };
    fetchProfile();
  }, []);

  const tabs = [
    { id: "agents", label: "Agents" },
    { id: "partners", label: "Partners" },
    { id: "all-players", label: "All Players" },
  ];

  const positions = [
    { id: "all", label: "All positions" },
    { id: "goalkeeper", label: "Goalkeeper" },
    { id: "defender", label: "Defender" },
    { id: "midfielder", label: "Midfielder" },
    { id: "center-forward", label: "Center-Forward" },
    { id: "attacker", label: "Attacker" },
  ];

  const handleShare = async (selectedUsers, shareUrl) => {
    try {
      console.log("Sharing with users:", selectedUsers);
      console.log("Share URL:", shareUrl);
      alert(tr(`Shared agency profile with ${selectedUsers.length} user(s)!`));
    } catch (error) {
      console.error("Error sharing:", error);
      alert(tr("Failed to share. Please try again."));
    }
  };

  const shareUrl = `${window.location.origin}/search/agency/${agencyId}`;

  // Helper function to get initials
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper component for image or initials
  const ImageOrInitials = ({ src, alt, size = "small" }) => {
    const sizeClasses = {
      small: "w-6 h-6",
      medium: "w-8 h-8",
      large: "w-10 h-10",
    };

    if (src) {
      return (
        <img
          src={src}
          alt={alt}
          className={`${sizeClasses[size]} rounded-full`}
        />
      );
    }

    return (
      <div
        className={`${sizeClasses[size]} rounded-full bg-gray-100 flex items-center justify-center text-secondary font-medium text-sm`}
      >
        {getInitials(alt)}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-red-500">
        Error: {error}
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Agency not found.
      </div>
    );
  }

  const filteredPlayers =
    selectedPosition === "all"
      ? agency.players
      : agency.players.filter(
          (p) => (p.position || "").toLowerCase() === selectedPosition
        );

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Header */}
      <div className="w-full px-4 md:px-12 py-4 flex items-center justify-between border-b border-[#E5E6E8] bg-white">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate(-1)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.8327 7.00033H1.16602M1.16602 7.00033L6.99935 12.8337M1.16602 7.00033L6.99935 1.16699"
              stroke="#1E1E1E"
              strokeWidth="1.875"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xl font-bold text-[#222A35]">
            {agency.name}
          </span>
        </div>
        {(() => {
          const worksForClub = currentUserProfile?.user_details?.works_for_club;
          const hideForStaffNoClub =
            currentRole === ROLES.Staff && worksForClub === false;
          if (hideForStaffNoClub) return null;
          return (
            <button
              type="button"
              className="border border-[#E6E6E6] rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
              onClick={() => setShowShareModal(true)}
              aria-label="Share agency profile"
            >
              <svg
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.72667 9.00634L9.28 11.6597M9.27333 4.33967L4.72667 6.99301M13 3.33301C13 4.43758 12.1046 5.33301 11 5.33301C9.89543 5.33301 9 4.43758 9 3.33301C9 2.22844 9.89543 1.33301 11 1.33301C12.1046 1.33301 13 2.22844 13 3.33301ZM5 7.99967C5 9.10424 4.10457 9.99967 3 9.99967C1.89543 9.99967 1 9.10424 1 7.99967C1 6.8951 1.89543 5.99967 3 5.99967C4.10457 5.99967 5 6.8951 5 7.99967ZM13 12.6663C13 13.7709 12.1046 14.6663 11 14.6663C9.89543 14.6663 9 13.7709 9 12.6663C9 11.5618 9.89543 10.6663 11 10.6663C12.1046 10.6663 13 11.5618 13 12.6663Z"
                  stroke="#2C2C2C"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-[#1E1E1E]">Share</span>
            </button>
          );
        })()}
      </div>

      {/* Hero Section Banner */}
      <div className="w-full px-4 md:px-10 pt-6 bg-white">
        <AgencyPublicBanner agency={agency} />
      </div>

      {/* Tabs */}
      <div className=" bg-white sticky top-0 z-20">
        {isMobile ? (
          <div className="w-full px-4 bg-white flex justify-between items-center border-b border-[#0000001A]">
            <div className="flex items-center gap-4">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      isActive
                        ? "py-4 text-xs border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                        : "py-4 text-xs text-[#1E1E1ECC]"
                    }`}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`panel-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="w-full mx-auto px-4 md:px-12">
            <div className="flex justify-between items-center">
              <div className="flex gap-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`my-4 py-2 px-2 rounded-lg font-medium relative ${
                      activeTab === tab.id
                        ? "bg-[#F3F4F6]"
                        : "text-[#0B0B0B] hover:text-gray-900"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="w-full mx-auto  px-4 md:px-12 py-8 bg-white">
        {/* Agents Tab Content */}
        {activeTab === "agents" && (
          <div className="space-y-6">
            {/* Sub-tabs for Agents */}
            <div className="flex justify-between items-center">
              <div className="inline-flex bg-[#F8F9FB] border border-[#E5E6E8] rounded-lg p-1">
                <button
                  className={`px-4 py-2 text-sm rounded-lg transition-all ${
                    activeAgentType === "licensed"
                      ? "bg-[#FFFFFF] text-black shadow-sm"
                      : "text-[#1E1E1ECC] text-sm hover:text-gray-900"
                  }`}
                  onClick={() => setActiveAgentType("licensed")}
                >
                  Licensed Agents
                </button>
                <button
                  className={`px-4 py-2 text-sm rounded-lg transition-all ${
                    activeAgentType === "unlicensed"
                      ? "bg-[#FFFFFF] text-black shadow-sm"
                      : "text-[#1E1E1ECC] text-sm hover:text-gray-900"
                  }`}
                  onClick={() => setActiveAgentType("unlicensed")}
                >
                  Unlicensed Agents
                </button>
              </div>
            </div>

            {/* Agents Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {agency.agents[activeAgentType].length > 0 ? (
                agency.agents[activeAgentType].map((agent, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-6 relative shadow-sm border border-[#D2D4D880]"
                  >
                    {/* No admin-action menu on the PUBLIC profile — promote/demote
                        lives on the private "My agency" page. The old 3-dot button
                        here had no handler and did nothing, so it's removed. */}
                    <div
                      onClick={() => {
                        if (agent?.account_id) {
                          navigate(`/search/agent/${agent?.account_id}`);
                        }
                      }}
                      className="cursor-pointer flex items-center gap-3 mb-4"
                    >
                      <ImageOrInitials
                        src={agent.profile_picture_url}
                        alt={agent.name}
                        size="medium"
                      />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#242426] text-[16px]">
                            {agent.name}
                          </span>
                          {agent.isAdmin && (
                            <span className="bg-gradient-to-r from-[rgba(126,139,255,0.2)]  to-[rgba(34,131,255,0.2)] text-[#374DF5] text-xs px-2 py-0.5 rounded">
                              Admin
                            </span>
                          )}
                          {/* FIFA badge on the public agent card — gate is
                              isFifaVerified (Marin 2026-08-04: the agent's
                              self-declared number carries it too; only
                              failed_qr hides it). */}
                          {isFifaVerified(agent) && (
                            <FifaVerifiedBadge size="md" />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4 bg-[#F7F7F7] px-5 py-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="text-[12px] text-[#242426B2]">
                          Phone
                        </div>
                        <div className="text-[12px] text-[#242426] mt-1">
                          {agent.phone_number || "N/A"}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-[12px] text-[#242426B2]">
                          Email
                        </div>
                        <div className="text-[12px] text-[#242426] mt-1">
                          {agent.email || "N/A"}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-[12px] text-[#242426B2]">
                          License
                        </div>
                        <div className="text-[12px] text-[#242426] mt-1">
                          {agent.license_number || "N/A"}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-[12px] text-[#242426B2]">
                          Active Players
                        </div>
                        <div className="text-[12px] text-[#242426] mt-1">
                          {agent.active_player_slots || "0"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-1 md:col-span-4 flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No{" "}
                    {activeAgentType === "licensed" ? "Licensed" : "Unlicensed"}{" "}
                    Agents
                  </h3>
                  <p className="text-gray-500 text-center">
                    No{" "}
                    {activeAgentType === "licensed" ? "licensed" : "unlicensed"}{" "}
                    agents found for this agency.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Partners Tab Content */}
        {activeTab === "partners" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {agency.partners.length > 0 ? (
              agency.partners.map((partner, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 relative shadow-sm border border-[#D2D4D880] cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex gap-3 items-center">
                      <ImageOrInitials
                        src={partner.profile_picture_url}
                        alt={partner.name}
                        size="medium"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#242426] text-[16px]">
                            {partner.name}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5"
                        fill="#2C2C2C"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 14a2 2 0 100-4 2 2 0 000 4zM19 14a2 2 0 100-4 2 2 0 000 4zM5 14a2 2 0 100-4 2 2 0 000 4z" />
                      </svg>
                    </div>
                  </div>
                  <div className="bg-[#F7F7F7] rounded-lg p-4 flex flex-col gap-2">
                    <div className="flex justify-between items-center ">
                      <span className="text-[#242426B2] text-xs">Phone</span>
                      <span className="text-[#242426] text-xs">
                        {partner.contact_person_phone_number || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center ">
                      <span className="text-[#242426B2] text-xs">Email</span>
                      <span className="text-[#242426] text-xs">
                        {partner.email || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-1 md:col-span-3 flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Partners
                </h3>
                <p className="text-gray-500 text-center">
                  No partners found for this agency.
                </p>
              </div>
            )}
          </div>
        )}

        {/* All Players Tab Content */}
        {activeTab === "all-players" && (
          <div className="space-y-6">
            {/* Position Filters */}
            <div className="flex justify-between items-center">
              {isMobile ? (
                <div className="w-full md:hidden">
                  <CustomDropdown
                    options={positions.map((p) => ({ value: p.id, label: p.label }))}
                    value={selectedPosition}
                    onChange={(val) => setSelectedPosition(val)}
                    placeholder="All positions"
                    className="flex items-center gap-2 p-3 bg-[#E9F3FF] text-[#2283FF] rounded-lg"
                    paddingY="py-2.5"
                  />
                </div>
              ) : (
                <div className="hidden md:inline-flex bg-[#F8F9FB] border border-[#E5E6E8] rounded-lg p-1">
                  {positions.map((position) => (
                    <button
                      key={position.id}
                      onClick={() => setSelectedPosition(position.id)}
                      className={`px-4 py-2 text-sm rounded-lg transition-all ${
                        selectedPosition === position.id
                          ? "bg-[#FFFFFF] text-black shadow-sm"
                          : "text-[#1E1E1ECC] text-sm hover:text-gray-900"
                      }`}
                    >
                      {position.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Players Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {filteredPlayers.length > 0 ? (
                filteredPlayers.map((player, index) => (
                  <AgentPlayerCard
                    key={index}
                    player={player}
                    showStatus={false}
                    showQuickActions={false}
                    showTransferFee={false}
                  />
                ))
              ) : (
                <div className="col-span-1 md:col-span-4 flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Players
                  </h3>
                  <p className="text-gray-500 text-center">
                    No players found for this agency.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ShareActionSheet
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        title={`Share ${agency?.name || "Agency"} Profile`}
        shareUrl={shareUrl}
        shareType="agency_public_profile"
        shareData={{
          agency: {
            id: agency?.id,
            name: agency?.name || "Agency Name",
          },
        }}
      />
    </div>
  );
};

export default AgencyPublicProfile;
